import api, { tokenManager } from './api';

export interface LoginData {
  username: string;
  password: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
}

export interface LoginResponse {
  success: boolean;
  data?: {
    user: User;
    tokens: {
      access_token: string;
      refresh_token: string;
    };
  };
  error?: string;
}

export interface VerifyResponse {
  success: boolean;
  data?: {
    valid: boolean;
    user: User;
  };
  error?: string;
}

class AuthService {
  /**
   * 로그인
   */
  async login(data: LoginData): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/login.php', data);
      
      if (response.data.success && response.data.data) {
        const { access_token, refresh_token } = response.data.data.tokens;
        tokenManager.setTokens(access_token, refresh_token);
        return response.data;
      }
      
      throw new Error(response.data.error || '로그인 실패');
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('로그인 중 오류가 발생했습니다.');
    }
  }

  /**
   * 로그아웃
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        await api.post('/auth/logout.php', { refresh_token: refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      tokenManager.clearTokens();
    }
  }

  /**
   * 토큰 검증 및 사용자 정보 가져오기
   */
  async verify(): Promise<User | null> {
    try {
      const token = tokenManager.getAccessToken();
      if (!token) {
        return null;
      }

      const response = await api.get<VerifyResponse>('/auth/verify.php');
      
      if (response.data.success && response.data.data?.valid) {
        return response.data.data.user;
      }
      
      tokenManager.clearTokens();
      return null;
    } catch (error) {
      console.error('Verify error:', error);
      tokenManager.clearTokens();
      return null;
    }
  }

  /**
   * 토큰 갱신
   */
  async refresh(): Promise<boolean> {
    try {
      const refreshToken = tokenManager.getRefreshToken();
      
      if (!refreshToken) {
        return false;
      }

      const response = await api.post('/auth/refresh.php', {
        refresh_token: refreshToken,
      });
      
      if (response.data.success && response.data.data) {
        const { access_token } = response.data.data;
        tokenManager.setTokens(access_token, refreshToken);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Refresh error:', error);
      tokenManager.clearTokens();
      return false;
    }
  }
}

export default new AuthService();
